import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { apiFetch } from '../lib/api'
import { loadGoogleMaps, MAPS_API_KEY } from '../lib/googleMaps'

const REFRESH_MS = 60 * 1000
const RADIUS_OPTIONS = [1, 3, 5, 10, 25]

function avatarUrl(user) {
  return user.profilePicture || `https://api.dicebear.com/7.x/thumbs/svg?seed=${encodeURIComponent(user.name || 'user')}`
}

function formatDistance(km) {
  if (km == null) return ''
  return km < 1 ? `${Math.round(km * 1000)} m away` : `${km.toFixed(1)} km away`
}

function formatLastSeen(iso) {
  if (!iso) return ''
  const mins = Math.floor((Date.now() - new Date(iso).getTime()) / 60000)
  if (mins <= 1) return 'just now'
  if (mins < 60) return `${mins} min ago`
  const hrs = Math.floor(mins / 60)
  return `${hrs} hr${hrs > 1 ? 's' : ''} ago`
}

/** Markup for an avatar map pin. `available` colours the ring green. */
function pinMarkup(user, { available, isMe }) {
  const ring = isMe ? '#2563eb' : available ? '#16a34a' : '#9ca3af'
  const size = isMe ? 46 : 42
  return `
    <div style="display:flex;flex-direction:column;align-items:center;filter:drop-shadow(0 4px 8px rgba(0,0,0,.28));">
      <div style="position:relative;width:${size}px;height:${size}px;">
        <div style="width:100%;height:100%;border-radius:9999px;background:#fff;border:3px solid ${ring};overflow:hidden;">
          <img src="${avatarUrl(user)}" alt="" style="width:100%;height:100%;object-fit:cover;display:block;" />
        </div>
        ${available && !isMe ? '<span style="position:absolute;top:0;right:0;width:12px;height:12px;border-radius:9999px;background:#16a34a;border:2px solid #fff;"></span>' : ''}
      </div>
      <div style="width:0;height:0;border-left:6px solid transparent;border-right:6px solid transparent;border-top:9px solid ${ring};margin-top:-1px;"></div>
      <span style="margin-top:2px;max-width:96px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;background:rgba(255,255,255,.95);border-radius:9999px;padding:1px 8px;font-size:11px;font-weight:700;color:#111827;">
        ${isMe ? 'You' : (user.name || '').split(' ')[0]}
      </span>
    </div>`
}

/** Builds an OverlayView subclass that renders arbitrary HTML at a LatLng. */
function makeAvatarOverlay(maps) {
  return class AvatarOverlay extends maps.OverlayView {
    constructor({ position, html, onClick, zIndex = 1 }) {
      super()
      this.position = position
      this.html = html
      this.onClick = onClick
      this.zIndex = zIndex
    }

    onAdd() {
      const div = document.createElement('div')
      div.style.position = 'absolute'
      div.style.transform = 'translate(-50%, -100%)'
      div.style.cursor = this.onClick ? 'pointer' : 'default'
      div.style.zIndex = String(this.zIndex)
      div.innerHTML = this.html
      if (this.onClick) div.addEventListener('click', (e) => { e.stopPropagation(); this.onClick() })
      maps.OverlayView.preventMapHitsAndGesturesFrom(div)
      this.div = div
      this.getPanes().floatPane.appendChild(div)
    }

    draw() {
      if (!this.div) return
      const point = this.getProjection()?.fromLatLngToDivPixel(this.position)
      if (!point) return
      this.div.style.left = `${point.x}px`
      this.div.style.top = `${point.y}px`
    }

    onRemove() {
      this.div?.remove()
      this.div = null
    }
  }
}

export default function NearbySpots({ currentUser, myCoords, locationError, onOpenProfile, onLogin }) {
  const [nearby, setNearby]         = useState([])
  const [radiusKm, setRadiusKm]     = useState(5)
  const [availableOnly, setAvailableOnly] = useState(false)
  const [loading, setLoading]       = useState(false)
  const [mapError, setMapError]     = useState('')
  const [mapReady, setMapReady]     = useState(false)
  const [selected, setSelected]     = useState(null)
  const [lastUpdated, setLastUpdated] = useState(null)

  const mapNodeRef   = useRef(null)
  const mapRef       = useRef(null)
  const mapsRef      = useRef(null)
  const overlayClsRef= useRef(null)
  const overlaysRef  = useRef([])
  const infoRef      = useRef(null)
  const circleRef    = useRef(null)
  const didFitRef    = useRef(false)
  const openProfileRef = useRef(onOpenProfile)

  useEffect(() => { openProfileRef.current = onOpenProfile }, [onOpenProfile])

  const myLat = myCoords?.latitude ?? null
  const myLng = myCoords?.longitude ?? null
  const hasMyCoords = myLat != null && myLng != null

  /* ── Load nearby diners ─────────────────────────────────────────────── */
  const loadNearby = useCallback(async () => {
    if (!currentUser) return
    setLoading(true)
    try {
      const params = new URLSearchParams({ radiusKm: String(radiusKm) })
      if (hasMyCoords) {
        params.set('lat', String(myLat))
        params.set('lng', String(myLng))
      }
      if (availableOnly) params.set('availableOnly', 'true')
      const res = await apiFetch(`/location/nearby?${params.toString()}`)
      if (res.ok) {
        const data = await res.json()
        setNearby(Array.isArray(data.users) ? data.users : [])
        setLastUpdated(new Date())
      }
    } catch { /* keep the previous markers on a failed refresh */ }
    finally { setLoading(false) }
  }, [currentUser, hasMyCoords, myLat, myLng, radiusKm, availableOnly])

  useEffect(() => {
    if (!currentUser) return
    // Deferred so the first fetch lands after this render commits
    queueMicrotask(loadNearby)
    const timer = setInterval(loadNearby, REFRESH_MS)
    return () => clearInterval(timer)
  }, [loadNearby, currentUser])

  /* ── Boot the map ───────────────────────────────────────────────────── */
  useEffect(() => {
    if (!currentUser) return
    let cancelled = false
    loadGoogleMaps()
      .then((maps) => {
        if (cancelled || !mapNodeRef.current || mapRef.current) return
        mapsRef.current = maps
        overlayClsRef.current = makeAvatarOverlay(maps)
        mapRef.current = new maps.Map(mapNodeRef.current, {
          center: { lat: 28.6139, lng: 77.209 }, // recentred on the user as soon as GPS lands
          zoom: 14,
          mapTypeControl: false,
          streetViewControl: false,
          fullscreenControl: false,
          clickableIcons: false,
        })
        infoRef.current = new maps.InfoWindow()
        infoRef.current.addListener('closeclick', () => setSelected(null))
        setMapReady(true)
      })
      .catch((err) => {
        if (!cancelled) {
          setMapError(
            MAPS_API_KEY
              ? 'Could not load Google Maps. Check the API key and its allowed referrers.'
              : 'Google Maps key missing — set VITE_GOOGLE_MAPS_API_KEY to see the map.'
          )
          console.warn('[maps]', err)
        }
      })
    return () => { cancelled = true }
  }, [currentUser])

  /* ── Centre on me the first time GPS arrives ────────────────────────── */
  useEffect(() => {
    if (!mapReady || !mapRef.current || !hasMyCoords || didFitRef.current) return
    mapRef.current.setCenter({ lat: myLat, lng: myLng })
    didFitRef.current = true
  }, [mapReady, hasMyCoords, myLat, myLng])

  /* ── Search-radius circle ───────────────────────────────────────────── */
  useEffect(() => {
    const maps = mapsRef.current
    if (!mapReady || !maps || !mapRef.current || !hasMyCoords) return
    const center = { lat: myLat, lng: myLng }
    if (!circleRef.current) {
      circleRef.current = new maps.Circle({
        map: mapRef.current,
        center,
        radius: radiusKm * 1000,
        strokeColor: '#f97316', strokeOpacity: 0.5, strokeWeight: 1,
        fillColor: '#f97316', fillOpacity: 0.06,
        clickable: false,
      })
    } else {
      circleRef.current.setCenter(center)
      circleRef.current.setRadius(radiusKm * 1000)
    }
  }, [mapReady, radiusKm, hasMyCoords, myLat, myLng])

  /* ── Render pins ────────────────────────────────────────────────────── */
  const openProfileCard = useCallback((user) => {
    const maps = mapsRef.current
    setSelected(user)
    if (!maps || !infoRef.current || !mapRef.current) return

    const node = document.createElement('div')
    node.style.minWidth = '210px'
    node.style.fontFamily = 'inherit'
    node.innerHTML = `
      <div style="display:flex;gap:10px;align-items:center;">
        <img src="${avatarUrl(user)}" alt="" style="width:52px;height:52px;border-radius:14px;object-fit:cover;background:#ffedd5;" />
        <div style="min-width:0;">
          <p style="margin:0;font-weight:700;font-size:14px;color:#111827;">${user.name || 'Diner'}</p>
          <p style="margin:2px 0 0;font-size:12px;color:#6b7280;">${formatDistance(user.distanceKm)} · ${formatLastSeen(user.lastSeenAt)}</p>
          <p style="margin:4px 0 0;font-size:11px;font-weight:700;color:${user.availability?.isAvailable ? '#16a34a' : '#9ca3af'};">
            ${user.availability?.isAvailable ? '🟢 Available to dine' : '⭕ Not available'}
          </p>
        </div>
      </div>
      <button type="button" data-profile-btn style="margin-top:10px;width:100%;border:0;border-radius:10px;padding:8px 10px;font-size:13px;font-weight:600;color:#fff;background:linear-gradient(90deg,#ef4444,#f97316);cursor:pointer;">
        View full profile
      </button>`
    node.querySelector('[data-profile-btn]')?.addEventListener('click', () => {
      infoRef.current?.close()
      openProfileRef.current?.(user.id)
    })

    infoRef.current.setContent(node)
    infoRef.current.setPosition({ lat: user.latitude, lng: user.longitude })
    infoRef.current.open({ map: mapRef.current })
    mapRef.current.panTo({ lat: user.latitude, lng: user.longitude })
  }, [])

  useEffect(() => {
    const maps = mapsRef.current
    const Overlay = overlayClsRef.current
    if (!mapReady || !maps || !Overlay || !mapRef.current) return

    overlaysRef.current.forEach((o) => o.setMap(null))
    overlaysRef.current = []

    if (hasMyCoords && currentUser) {
      const mine = new Overlay({
        position: new maps.LatLng(myLat, myLng),
        html: pinMarkup(currentUser, { available: currentUser.availability?.isAvailable, isMe: true }),
        zIndex: 5,
      })
      mine.setMap(mapRef.current)
      overlaysRef.current.push(mine)
    }

    nearby.forEach((user) => {
      const overlay = new Overlay({
        position: new maps.LatLng(user.latitude, user.longitude),
        html: pinMarkup(user, { available: user.availability?.isAvailable, isMe: false }),
        onClick: () => openProfileCard(user),
      })
      overlay.setMap(mapRef.current)
      overlaysRef.current.push(overlay)
    })

    return () => {
      overlaysRef.current.forEach((o) => o.setMap(null))
      overlaysRef.current = []
    }
  }, [mapReady, nearby, hasMyCoords, myLat, myLng, currentUser, openProfileCard])

  const availableCount = useMemo(
    () => nearby.filter((u) => u.availability?.isAvailable).length,
    [nearby]
  )

  if (!currentUser) {
    return (
      <section>
        <div className="fade-up glass-card rounded-3xl border border-white/60 p-8 text-center shadow-xl">
          <h2 className="title-font text-2xl font-bold text-gray-900 mb-2">Nearby Spots 📍</h2>
          <p className="text-sm text-gray-600 mb-5">Sign in to see diners available around you on the map.</p>
          <button onClick={onLogin} className="rounded-2xl bg-gradient-to-r from-red-500 to-orange-500 px-6 py-2.5 text-sm font-semibold text-white">
            Sign in
          </button>
        </div>
      </section>
    )
  }

  return (
    <section className="space-y-4">
      <div className="fade-up glass-card rounded-3xl border border-white/60 p-5 shadow-xl">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2 className="title-font text-2xl font-bold text-gray-900">Nearby Spots 📍</h2>
            <p className="text-sm text-gray-600">
              {nearby.length} diner{nearby.length !== 1 ? 's' : ''} within {radiusKm} km ·{' '}
              <span className="font-semibold text-green-600">{availableCount} available</span>
            </p>
          </div>
          <div className="flex items-center gap-2">
            {loading && <span className="text-xs text-gray-500">Refreshing…</span>}
            {lastUpdated && !loading && (
              <span className="text-xs text-gray-400">Updated {lastUpdated.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}</span>
            )}
            <button onClick={loadNearby} className="rounded-xl border border-gray-200 bg-white px-3 py-1.5 text-xs font-semibold text-gray-700 hover:bg-gray-50">
              Refresh
            </button>
          </div>
        </div>

        <div className="mt-4 flex flex-wrap items-center gap-2">
          {RADIUS_OPTIONS.map((km) => (
            <button
              key={km}
              onClick={() => setRadiusKm(km)}
              className={`rounded-full px-3 py-1.5 text-xs font-semibold transition ${
                km === radiusKm ? 'bg-orange-500 text-white' : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'
              }`}
            >
              {km} km
            </button>
          ))}
          <label className="ml-auto flex items-center gap-2 text-xs font-semibold text-gray-700">
            <input type="checkbox" checked={availableOnly} onChange={(e) => setAvailableOnly(e.target.checked)} className="accent-orange-500" />
            Available only
          </label>
        </div>

        {locationError && (
          <p className="mt-3 rounded-2xl bg-amber-50 border border-amber-200 px-4 py-2 text-xs text-amber-800">
            Location access is off, so others can’t see you and the map can’t centre on you. Allow location in your browser to join the map.
          </p>
        )}
        {mapError && (
          <p className="mt-3 rounded-2xl bg-red-50 border border-red-200 px-4 py-2 text-xs text-red-700">{mapError}</p>
        )}

        <div className="mt-4 overflow-hidden rounded-2xl border border-gray-200">
          <div ref={mapNodeRef} className="h-[420px] w-full bg-gray-100" />
        </div>
        <p className="mt-2 text-xs text-gray-500">
          Your location refreshes every minute while Co-Eater is open. Only your current position is stored — never a history.
        </p>
      </div>

      <div className="fade-up glass-card rounded-3xl border border-white/60 p-5 shadow-xl">
        <h3 className="title-font text-lg font-bold text-gray-900 mb-3">Closest to you</h3>
        {nearby.length === 0 ? (
          <p className="text-sm text-gray-600">
            No one is sharing their location around you right now. Try a bigger radius.
          </p>
        ) : (
          <div className="grid gap-3 sm:grid-cols-2">
            {nearby.map((user) => (
              <button
                key={user.id}
                onClick={() => openProfileCard(user)}
                className="flex items-center gap-3 rounded-2xl border border-gray-200 bg-white px-4 py-3 text-left transition hover:border-orange-300 hover:bg-orange-50"
              >
                <img src={avatarUrl(user)} alt={user.name} className="h-12 w-12 rounded-xl object-cover bg-orange-100" />
                <div className="min-w-0 flex-1">
                  <p className="truncate font-semibold text-gray-900">{user.name}</p>
                  <p className="text-xs text-gray-500">{formatDistance(user.distanceKm)} · {formatLastSeen(user.lastSeenAt)}</p>
                </div>
                <span className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-bold ${user.availability?.isAvailable ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                  {user.availability?.isAvailable ? 'Available' : 'Busy'}
                </span>
              </button>
            ))}
          </div>
        )}
        {selected && (
          <button
            onClick={() => onOpenProfile?.(selected.id)}
            className="mt-4 w-full rounded-2xl bg-gradient-to-r from-red-500 to-orange-500 px-4 py-2.5 text-sm font-semibold text-white"
          >
            Open {selected.name}’s profile
          </button>
        )}
      </div>
    </section>
  )
}
